/* CISTA HTML5 media policy. Component-owned films retain their own playback controllers. */
(() => {
  if (window.CistaMedia) return;

  const INITIAL_VOLUME = 0.3;
  const MOAST = 'moast-carousel, moast-media, moast-video, moast-player';
  const MANAGED = 'cista-proof, cista-campaign-film, cista-size-films, [data-cista-managed], ' + MOAST;
  const shadowRoots = new WeakSet();
  const shadowHosts = new WeakSet();
  const states = new Map();
  const records = new WeakMap();
  const deferred = new Set();
  let audioContext;
  let updateFrame = 0;

  function isManaged(video) {
    let node = video;
    while (node) {
      if (node.closest(MANAGED)) return true;
      node = node.getRootNode ? node.getRootNode().host : null;
    }
    return false;
  }
  const setInitialVolume = (video) => {
    try { video.volume = INITIAL_VOLUME; } catch (_) { /* Some mobile browsers own volume. */ }
  };

  function mute(video) {
    const state = states.get(video);
    if (state) state.lastMuted = true;
    video.muted = true;
  }

  function muteOthers(video) {
    states.forEach((_, other) => { if (other !== video && !other.muted) mute(other); });
  }

  function safeAudioSource(video, state) {
    const source = video.currentSrc || video.src || (video.querySelector('source[src]') || {}).src;
    if (!source) return false;
    try {
      if (new URL(source, document.baseURI).origin === window.location.origin) return true;
      // A loaded CORS resource, or a CORS attribute captured before its first load,
      // is safe to route through Web Audio.
      // Never change crossOrigin after loading: that can reload or silence an app's video.
      return video.crossOrigin === 'anonymous' && (video.readyState >= 1 || state.corsBeforeLoad);
    } catch (_) { return false; }
  }

  function prepareAudio(video, state) {
    // On iOS, assigning volume may be ignored. Use gain only in that case and only
    // after a sound/play-control gesture; unsupported external players keep native audio.
    if (Math.abs(video.volume - INITIAL_VOLUME) < 0.01) return;
    const Context = window.AudioContext || window.webkitAudioContext;
    if (!Context || !safeAudioSource(video, state)) return;
    try {
      if (!state.audio) {
        audioContext ||= new Context();
        const gain = audioContext.createGain();
        gain.gain.value = INITIAL_VOLUME;
        gain.connect(audioContext.destination);
        const source = audioContext.createMediaElementSource(video);
        source.connect(gain);
        state.audio = { source, gain };
      }
      if (audioContext.state === 'suspended') {
        const resumed = audioContext.resume();
        if (resumed && typeof resumed.catch === 'function') resumed.catch(() => mute(video));
      }
    } catch (_) {
      // A third-party player may already own a MediaElementSource. Do not replace it.
    }
  }

  function setMuted(video, shouldMute) {
    if (!video || video.tagName !== 'VIDEO') return true;
    const state = initialize(video);
    if (shouldMute) {
      mute(video);
    } else {
      setInitialVolume(video);
      prepareAudio(video, state);
      muteOthers(video);
      state.lastMuted = false;
      video.muted = false;
    }
    return video.muted;
  }

  function visible(node) {
    if (!node.isConnected || document.hidden || !node.getClientRects().length) return false;
    if (node.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    const style = window.getComputedStyle(node);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    const modal = node.closest('product-modal, modal-dialog');
    if (modal && !modal.hasAttribute('open')) return false;
    const media = node.closest('.product__media-item');
    const gallery = node.closest('media-gallery');
    if (media && gallery && window.matchMedia('(min-width: 750px)').matches &&
        (gallery.dataset.desktopLayout || '').includes('thumbnail') && !media.classList.contains('is-active')) return false;
    const rect = node.getBoundingClientRect();
    const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    return rect.width > 0 && rect.height > 0 && width * height / (rect.width * rect.height) >= 0.35;
  }

  function pause(video, state) {
    state.generation += 1;
    if (!video.paused) {
      state.expectedPauses += 1;
      video.pause();
    }
    mute(video);
  }

  function reconcile(video, state) {
    if (!video.isConnected) return;
    if (isManaged(video)) {
      // Audio must never resume merely because someone scrolls back to a film.
      if (!visible(video) && !video.muted) mute(video);
      return;
    }
    if (!visible(video)) {
      pause(video, state);
      return;
    }
    if (state.manualPaused || state.pending || !video.paused || (video.ended && !video.loop)) return;
    mute(video);
    state.pending = true;
    const generation = ++state.generation;
    let playing;
    try { playing = video.play(); } catch (_) { state.pending = false; return; }
    Promise.resolve(playing).then(() => {
      state.pending = false;
      if (generation !== state.generation || !visible(video) || state.manualPaused) pause(video, state);
    }).catch(() => { state.pending = false; });
  }

  function initialize(video) {
    if (states.has(video)) return states.get(video);
    if (records.has(video)) {
      const existing = records.get(video);
      states.set(video, existing);
      if (observer) observer.observe(video);
      return existing;
    }
    const state = {
      manualPaused: false, expectedPauses: 0, pending: false, generation: 0,
      lastMuted: true, soundGestureUntil: 0, audio: null,
      corsBeforeLoad: video.crossOrigin === 'anonymous' && video.readyState === 0
    };
    states.set(video, state);
    records.set(video, state);
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    setInitialVolume(video);
    // Managed sequences may preload other scenes. Their controller alone decides when to play.
    if (!isManaged(video)) video.autoplay = true;
    video.addEventListener('volumechange', () => {
      if (!video.muted && state.lastMuted) {
        if (Date.now() > state.soundGestureUntil) { mute(video); return; }
        setInitialVolume(video);
        muteOthers(video);
      }
      state.lastMuted = video.muted;
    });
    video.addEventListener('play', () => {
      state.manualPaused = false;
      if (!isManaged(video) && !visible(video)) pause(video, state);
    });
    video.addEventListener('pause', () => {
      if (state.expectedPauses > 0) { state.expectedPauses -= 1; return; }
      if (!isManaged(video) && visible(video) && !video.ended) state.manualPaused = true;
    });
    video.addEventListener('ended', () => { state.manualPaused = false; });
    video.addEventListener('loadedmetadata', schedule);
    // Native controls live in the browser's shadow UI. Prime the optional iOS gain
    // in the original gesture, so its later volumechange can safely enable audio.
    const prime = () => {
      if (video.controls && video.muted) {
        state.soundGestureUntil = Date.now() + 1500;
        setInitialVolume(video);
        prepareAudio(video, state);
      }
    };
    video.addEventListener('pointerdown', prime);
    video.addEventListener('keydown', prime);
    video.addEventListener('click', prime);
    if (observer) observer.observe(video);
    return state;
  }

  function loadVisibleDeferred(node) {
    if (!visible(node) || node.hasAttribute('loaded') || isManaged(node)) return;
    const template = node.querySelector('template');
    const original = template && template.content.querySelector('video');
    if (!original) return; // Native models and external iframe players retain their own controls.
    const video = original.cloneNode(true);
    // Configure before insertion; Dawn's loadContent also pauses unrelated page videos.
    initialize(video);
    node.setAttribute('loaded', 'true');
    node.appendChild(video);
  }

  function reconcileAll() {
    updateFrame = 0;
    deferred.forEach((node) => {
      if (!node.isConnected) { deferred.delete(node); if (observer) observer.unobserve(node); return; }
      loadVisibleDeferred(node);
    });
    states.forEach((state, video) => {
      if (!video.isConnected) {
        pause(video, state);
        if (observer) observer.unobserve(video);
        states.delete(video);
      } else reconcile(video, state);
    });
  }

  function schedule() {
    if (!updateFrame) updateFrame = window.requestAnimationFrame(reconcileAll);
  }

  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(schedule, {
    threshold: [0, 0.1, 0.35, 0.5, 1]
  }) : null;

  function watchMoast(host) {
    if (shadowHosts.has(host)) return;
    shadowHosts.add(host);
    const connect = () => {
      const root = host.shadowRoot;
      if (!root || shadowRoots.has(root)) return;
      shadowRoots.add(root);
      mutations.observe(root, mutationOptions);
      // Moast owns autoplay/sequencing. Only initialize its open-shadow media policy.
      const primeMoast = () => {
        states.forEach((state, video) => {
          if (video.paused || !video.muted || !visible(video)) return;
          let node = video;
          while (node) {
            const containingRoot = node.getRootNode ? node.getRootNode() : null;
            if (containingRoot === root) {
              state.soundGestureUntil = Date.now() + 1500;
              setInitialVolume(video);
              prepareAudio(video, state);
              break;
            }
            node = containingRoot && containingRoot.host;
          }
        });
      };
      ['pointerdown', 'keydown', 'click'].forEach((event) => root.addEventListener(event, primeMoast, { capture: true }));
      scan(root);
      schedule();
    };
    if (host.shadowRoot) connect();
    else if (window.customElements) {
      window.customElements.whenDefined(host.localName).then(() => Promise.resolve(host.updateComplete)).then(connect).catch(() => {});
    }
  }

  function scan(root) {
    if (root.nodeType !== 1 && root.nodeType !== 11 && root !== document) return;
    if (root.matches && root.matches('video')) initialize(root);
    root.querySelectorAll('video').forEach(initialize);
    root.querySelectorAll(MOAST).forEach(watchMoast);
    if (root.matches && root.matches(MOAST)) watchMoast(root);
    const candidates = Array.from(root.querySelectorAll('deferred-media'));
    if (root.matches && root.matches('deferred-media')) candidates.push(root);
    candidates.forEach((node) => {
      if (deferred.has(node)) return;
      deferred.add(node);
      if (observer) observer.observe(node);
    });
  }

  window.CistaMedia = { setMuted, initialize };
  const mutationOptions = {
    subtree: true, childList: true, attributes: true,
    attributeFilter: ['class', 'hidden', 'open', 'aria-hidden', 'inert']
  };
  const mutations = new MutationObserver((changes) => {
    changes.forEach((change) => {
      if (change.type === 'childList') change.addedNodes.forEach(scan);
    });
    schedule();
  });
  mutations.observe(document.documentElement, mutationOptions);
  scan(document);
  document.addEventListener('visibilitychange', schedule);
  document.addEventListener('shopify:section:load', (event) => { scan(event.target); schedule(); });
  document.addEventListener('scroll', schedule, { passive: true, capture: true });
  window.addEventListener('resize', schedule, { passive: true });
  schedule();
})();
